import type { ReactElement, ReactNode } from "react";

interface TabRailTabBaseProps {
  /** Identifies this tab; matched against `TabRail`'s `activeTabId` and passed to `onSelectTab`. */
  value: string;
  /**
   * Classes merged onto the rendered tab after the rail's own. The tab carries
   * `data-state="active" | "inactive"`, so selected-only styling uses `data-[state=active]:`.
   */
  className?: string;
  /** A static Pendo tracking id for the tab. */
  "data-trackingid"?: string;
}

interface TabRailManagedTabProps extends TabRailTabBaseProps {
  asChild?: false;
  /** The tab's label. Plain text here because it doubles as the rename draft and the delete button's label. */
  children: string;
  /** When provided, a small "x" appears on hover/focus to remove this tab. */
  onDelete?: () => void;
  /**
   * When provided, double-clicking this tab's label enters inline rename mode. Enter/blur commits
   * the trimmed value; Escape cancels. Omit to leave this tab read-only.
   */
  onRename?: (label: string) => void;
}

interface TabRailRichTabProps extends TabRailTabBaseProps {
  asChild?: false;
  /** The tab's content: a label with any icon, marker or badge alongside it. */
  children: ReactNode;
  onDelete?: never;
  onRename?: never;
}

interface TabRailLinkTabProps extends TabRailTabBaseProps {
  /**
   * Renders the tab onto its only child, the app's router `Link`, making it a navigation link.
   * Every tab in a rail must be a link, or none may be: a rail of links renders as a `nav` with
   * `aria-current="page"` on the active link instead of a `tablist`.
   */
  asChild: true;
  /** The router `Link`; its own children are the label. */
  children: ReactElement;
  onDelete?: never;
  onRename?: never;
}

export type TabRailTabProps = TabRailManagedTabProps | TabRailRichTabProps | TabRailLinkTabProps;

// `TabRail.Tab` is a config-only descriptor, not a real rendered component -- `TabRail.List`
// reads these props directly off the element (via `Children.toArray`) before anything mounts, and
// renders the actual tab + its delete button itself. That's not a style choice: the delete button
// must be a DOM *sibling* of its tab sharing the same grid cell, never a descendant (see
// TabRailList.tsx) -- an ARIA constraint that a normal "each child renders its own subtree"
// component couldn't satisfy. This function body is never invoked by React; it exists only so
// `<TabRail.Tab>` type-checks and reads naturally as JSX.
export const TabRailTabPart = (_props: TabRailTabProps) => null;
